import { apiFetch } from "@/lib/api";

import type { KittingPickDataApiResponse } from "./kittingPickDataApi";

export const KITTING_PICK_RUN_QUERY_KEY = "run";

export interface KittingPickInventoryContainer {
  hex: string;
  shortCode: string;
  quantity: number;
  picked?: boolean;
}

export interface KittingPickRunItem {
  id: number;
  slotKey: string;
  buildOrderId: number | null;
  transferOrderId: number | null;
  componentSku: string;
  pickedQuantity: number;
  absorbedQuantity: number;
  unabsorbedVirtual: number;
  inventoryContainers?: KittingPickInventoryContainer[];
}

export interface KittingPickRun {
  id: number;
  status: "open" | "completed";
  slotKeys: string[];
  cartMarks: Record<string, string>;
  items: KittingPickRunItem[];
  /** Picks from earlier runs that still count against the line. */
  priorUnabsorbedItems?: KittingPickRunItem[];
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface KittingPickRunFinishWarning {
  warning: "cart_has_quantities";
  message: string;
  run: KittingPickRun;
  totalPickedQuantity: number;
}

export interface KittingPickRunActionResponse {
  ok: true;
  buildOrderId: number;
  run: KittingPickRun;
  pickData: KittingPickDataApiResponse;
  packing_quantity_request?: { id: string; requested_quantity: number } | null;
  inventory_discrepancy_request?: { id: string; quantity: number } | null;
}

export interface KittingPickRunCancelResponse {
  ok: true;
  pickData: KittingPickDataApiResponse;
  slotKeys: string[];
}

export function kittingPickRunQueryKey(runId: number | null): readonly (string | number | null)[] {
  return ["manufacturing", "kitting", "pick-run", runId];
}

export function kittingCurrentPickRunQueryKey(): readonly string[] {
  return ["manufacturing", "kitting", "pick-run", "current"];
}

export function buildKittingPickPageRunSearchParams(runId: number): URLSearchParams {
  const params = new URLSearchParams();
  params.set(KITTING_PICK_RUN_QUERY_KEY, String(runId));
  return params;
}

export function parseKittingPickRunId(raw: string | null): number | null {
  if (raw == null || raw.trim() === "") return null;
  const runId = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(runId) && runId > 0 ? runId : null;
}

export async function fetchCurrentKittingPickRun(): Promise<KittingPickRun | null> {
  const response = await apiFetch<{ run: KittingPickRun | null }>("/api/manufacturing/kitting/pick-run/current");
  return response.run;
}

export async function fetchKittingPickRun(runId: number): Promise<KittingPickRun> {
  const response = await apiFetch<{ run: KittingPickRun }>(`/api/manufacturing/kitting/pick-run/${String(runId)}`);
  return response.run;
}

export async function createKittingPickRun(input: {
  slotKeys: readonly string[];
  cartMarks: Record<string, string>;
}): Promise<KittingPickRun> {
  const response = await apiFetch<{ run: KittingPickRun }>("/api/manufacturing/kitting/pick-run", {
    method: "POST",
    body: {
      slotKeys: [...input.slotKeys],
      cartMarks: input.cartMarks,
    },
  });
  return response.run;
}

export async function finishKittingPickRun(
  runId: number,
  options?: { confirmCartNotEmpty?: boolean },
): Promise<KittingPickRun> {
  const response = await apiFetch<{ run: KittingPickRun }>(
    `/api/manufacturing/kitting/pick-run/${String(runId)}/finish`,
    {
      method: "POST",
      body: {
        confirmCartNotEmpty: options?.confirmCartNotEmpty === true,
      },
    },
  );
  return response.run;
}

export async function cancelKittingPickRun(runId: number): Promise<KittingPickRunCancelResponse> {
  return apiFetch<KittingPickRunCancelResponse>(
    `/api/manufacturing/kitting/pick-run/${String(runId)}/cancel`,
    { method: "POST", body: {} },
  );
}

export function isKittingPickRunFinishWarning(error: unknown): error is KittingPickRunFinishWarning {
  if (error == null || typeof error !== "object") return false;
  const record = error as Record<string, unknown>;
  return record.warning === "cart_has_quantities" && record.run != null;
}
